import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormikSelect from '../../../../../base/forms/formikSelect';
import media from '../../../../../../constants/media';
import { IFormSelectOption } from '../../../../../../interfaces/forms/forms';
import {
	WEIGHT_CATEGORY,
	WeightOption,
	DOMESTIC_PARCEL_OPTIONS,
	INTERNATIONAL_PARCEL_OPTIONS,
	DOMESTIC_LETTER_OPTIONS,
	INTERNATIONAL_LETTER_OPTIONS,
} from '../constants/weightCategories';

interface Props {
	service: 'Parcel' | 'Letter' | '';
	countryCode: string;
	selectedValue: string;
	onChange: (weightCategory: string, parcelWeight: number, dontKnowWeight: boolean) => void;
	onBlur: any;
	customWeight: string;
	onCustomWeightChange: (value: string) => void;
}

const Wrapper = styled.div`
	margin-bottom: 2rem;
`;

const DropdownRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 2rem;
`;

const DropdownColumn = styled.div`
	width: 50%;
	min-width: 20rem;
	padding-right: 15px;

	${media.mobileOnly`
		> div {
			font-size: 1.6rem;
		}
	`};
`;

const InfoColumn = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	min-height: 5rem;
	padding-top: 0.5rem;
`;

const InfoText = styled.p`
	font-size: 1.4rem;
	color: #666;
	margin: 0;
`;

const CustomWeightWrapper = styled.div`
	margin-top: 1.5rem;
	position: relative;
	width: 50%;
	padding-right: 15px;
`;

const CustomWeightInput = styled.input`
	display: block;
	width: 100%;
	height: 5rem;
	padding: 0 2.5rem;
	font-size: 1.7rem;
	color: #333;
	border: 1px solid #ccc;
	border-radius: 2px;
	transition: border-color 100ms;

	&:focus,
	&:hover {
		border-color: #999;
	}

	&::-webkit-outer-spin-button,
	&::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	-moz-appearance: textfield;
`;

const UnitLabel = styled.span`
	position: absolute;
	right: 3rem;
	top: 50%;
	transform: translateY(-50%);
	color: #333;
	pointer-events: none;
	user-select: none;
`;

export function getWeightOptions(service: string, countryCode: string, t: (key: string) => string): WeightOption[] {
	const heavyOption: WeightOption = { label: t('step-shipment.heavy-option'), value: WEIGHT_CATEGORY.HEAVY, weight: null, unit: 'kg' };
	const unknownOption: WeightOption = { label: t('step-shipment.dont-know-weight'), value: WEIGHT_CATEGORY.UNKNOWN, weight: null, unit: null };

	if (service === 'Parcel' && countryCode !== 'IS') {
		return [...INTERNATIONAL_PARCEL_OPTIONS, heavyOption, unknownOption];
	}

	if (service === 'Parcel' && countryCode === 'IS') {
		return [...DOMESTIC_PARCEL_OPTIONS, heavyOption, unknownOption];
	}

	if (service === 'Letter' && countryCode === 'IS') {
		return [...DOMESTIC_LETTER_OPTIONS];
	}

	if (service === 'Letter' && countryCode !== 'IS') {
		return [...INTERNATIONAL_LETTER_OPTIONS];
	}

	return [];
}

const WeightDropdown: FC<Props> = ({
	service,
	countryCode,
	selectedValue,
	onChange,
	onBlur,
	customWeight,
	onCustomWeightChange,
}) => {
	const { t } = useTranslation('registerShipment');

	const options = getWeightOptions(service, countryCode, t);
	const selectOptions: IFormSelectOption[] = options.map(opt => ({
		label: opt.label,
		value: opt.value,
	}));

	const currentOption = selectOptions.find(o => o.value === selectedValue) || null;

	const handleChange = (_name: string, selected: IFormSelectOption) => {
		if (!selected) return;

		const option = options.find(o => o.value === selected.value);
		if (!option) return;

		if (option.value === WEIGHT_CATEGORY.UNKNOWN) {
			onChange(option.value, 0, true);
		} else if (option.value === WEIGHT_CATEGORY.HEAVY) {
			onChange(option.value, 0, false);
		} else {
			onChange(option.value, option.weight, false);
		}
	};

	const isLetter = service === 'Letter';
	const isParcel = service === 'Parcel';
	const customWeightRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (selectedValue === WEIGHT_CATEGORY.HEAVY && customWeightRef.current) {
			customWeightRef.current.focus();
		}
	}, [selectedValue]);

	return (
		<Wrapper>
			<DropdownRow>
				<DropdownColumn>
					<FormikSelect
						id="weightCategory"
						name="weightCategory"
						value={currentOption}
						options={selectOptions}
						onBlur={onBlur}
						placeholder={t('step-shipment.weight-placeholder')}
						onChange={handleChange}
					/>
				</DropdownColumn>
				<InfoColumn>
					{isLetter && <InfoText>{t('step-shipment.max-letter-weight-short')}</InfoText>}
					{isParcel && selectedValue && selectedValue !== WEIGHT_CATEGORY.UNKNOWN && (
						<InfoText>{t('step-shipment.round-up-text')}</InfoText>
					)}
				</InfoColumn>
			</DropdownRow>
			{(selectedValue === WEIGHT_CATEGORY.UNKNOWN || !selectedValue) && (
				<InfoText style={{ color: 'red', fontWeight: 'bold', marginTop: '1rem' }}>
					{t('step-shipment.weight-unknown-info')}
				</InfoText>
			)}
			{selectedValue === WEIGHT_CATEGORY.HEAVY && (
				<CustomWeightWrapper>
					<CustomWeightInput
						ref={customWeightRef}
						type="text"
						inputMode="decimal"
						placeholder={t('step-shipment.enter-weight-kg')}
						value={customWeight}
						onChange={e => {
							const val = e.target.value.replace(/[^0-9.,]/g, '');
							onCustomWeightChange(val);
						}}
					/>
					<UnitLabel>kg</UnitLabel>
				</CustomWeightWrapper>
			)}
		</Wrapper>
	);
};

export default WeightDropdown;
